<template>
  <div class="pnl-page-container">
    <vue-element-loading
      :active="appLoading"
      :is-full-screen="true"
      background-color="#FFFFFF"
      color="#1e293b"
      spinner="bar-fade-scale"
    />
    <ServerError v-if="ServerError" />

    <v-snackbar v-model="showsnackbar" color="#1e293b" right :timeout="timeout">
      <v-layout wrap justify-center>
        <v-flex text-left class="align-self-center">
          <span style="color: white; font-weight: 600;">
            {{ msg }}
          </span>
        </v-flex>
        <v-flex text-right>
          <v-btn small :ripple="false" text @click="showsnackbar = false">
            <v-icon style="color: #ffffff">mdi-close</v-icon>
          </v-btn>
        </v-flex>
      </v-layout>
    </v-snackbar>

    <!-- 1. HERO HEADER SECTION -->
    <header class="pnl-hero-header mb-4">
      <div class="pnl-hero-text">
        <PageBreadcrumb :items="[{ text: 'Accounts', to: '/Accounts' }, { text: 'Profit & Loss' }]" class="mb-1" />
        <div class="d-flex align-center flex-wrap gap-2">
          <div class="date-badge-pill">
            <v-icon small color="#475569" class="mr-1">mdi-calendar-range</v-icon>
            <span>{{ formatDateDisplay(from) }} &mdash; {{ formatDateDisplay(to) }}</span>
          </div>
        </div>
        <p class="pnl-subtitle">
          Summary of operating revenues, expenses, and net profit or loss
        </p>
      </div>

      <div class="pnl-hero-actions">
        <button class="action-btn outline-btn mr-2" @click="fetchProfitAndLoss()" title="Refresh Statement">
          <v-icon left size="16" color="#334155">mdi-refresh</v-icon>
          Refresh
        </button>
        <button
          class="action-btn primary-dark-btn"
          :disabled="isDownloading"
          @click="download()"
        >
          <v-icon left size="17" color="#ffffff">
            {{ isDownloading ? 'mdi-loading mdi-spin' : 'mdi-file-pdf-box' }}
          </v-icon>
          {{ isDownloading ? 'Exporting...' : 'Download PDF' }}
        </button>
      </div>
    </header>

    <!-- 2. DATE FILTER CONTROLS -->
    <section class="pnl-filter-card mb-4">
      <div class="filter-card-right">
        <!-- From Date -->
        <div class="date-field-wrapper">
          <span class="date-label">From:</span>
          <v-menu
            ref="fromMenu"
            v-model="fromMenu"
            :close-on-content-click="false"
            :return-value.sync="from"
            transition="scale-transition"
            offset-y
            min-width="auto"
          >
            <template v-slot:activator="{ on, attrs }">
              <div class="custom-date-box" v-bind="attrs" v-on="on">
                <v-icon size="15" color="#475569" class="mr-1">mdi-calendar-start</v-icon>
                <span class="date-box-val">{{ from }}</span>
                <v-icon size="14" color="#94a3b8" class="ml-1">mdi-chevron-down</v-icon>
              </div>
            </template>
            <v-date-picker
              v-model="from"
              color="#1e293b"
              no-title
              scrollable
              @change="onFromDateChange(from)"
            >
              <v-spacer></v-spacer>
            </v-date-picker>
          </v-menu>
        </div>

        <!-- To Date -->
        <div class="date-field-wrapper">
          <span class="date-label">To:</span>
          <v-menu
            ref="toMenu"
            v-model="toMenu"
            :close-on-content-click="false"
            :return-value.sync="to"
            transition="scale-transition"
            offset-y
            min-width="auto"
          >
            <template v-slot:activator="{ on, attrs }">
              <div class="custom-date-box" v-bind="attrs" v-on="on">
                <v-icon size="15" color="#475569" class="mr-1">mdi-calendar-end</v-icon>
                <span class="date-box-val">{{ to }}</span>
                <v-icon size="14" color="#94a3b8" class="ml-1">mdi-chevron-down</v-icon>
              </div>
            </template>
            <v-date-picker
              v-model="to"
              color="#1e293b"
              no-title
              scrollable
              @change="onToDateChange(to)"
            >
              <v-spacer></v-spacer>
            </v-date-picker>
          </v-menu>
        </div>
      </div>
    </section>

    <!-- 3. TOP EXECUTIVE KPI METRICS (4 CARDS) -->
    <section class="pnl-kpi-grid mb-4">
      <!-- 1. Total Income -->
      <div class="pnl-kpi-card">
        <div class="kpi-top">
          <div class="kpi-icon-box">
            <v-icon size="20" color="#334155">mdi-arrow-top-right</v-icon>
          </div>
          <span class="kpi-pill">Revenue</span>
        </div>
        <div class="kpi-label">TOTAL INCOME</div>
        <div class="kpi-value">₹ {{ formatCurrency(totalIncome) }}</div>
        <div class="kpi-footer">
          <span class="kpi-meta">
            {{ incomeRows.length }} income {{ incomeRows.length === 1 ? 'head' : 'heads' }}
          </span>
        </div>
      </div>

      <!-- 2. Total Expense -->
      <div class="pnl-kpi-card">
        <div class="kpi-top">
          <div class="kpi-icon-box">
            <v-icon size="20" color="#334155">mdi-arrow-bottom-right</v-icon>
          </div>
          <span class="kpi-pill">Expenses</span>
        </div>
        <div class="kpi-label">TOTAL EXPENSES</div>
        <div class="kpi-value">₹ {{ formatCurrency(totalExpense) }}</div>
        <div class="kpi-footer">
          <span class="kpi-meta">
            {{ expenseRows.length }} expense {{ expenseRows.length === 1 ? 'head' : 'heads' }}
          </span>
        </div>
      </div>

      <!-- 3. Net Result (Profit or Loss) -->
      <div class="pnl-kpi-card">
        <div class="kpi-top">
          <div class="kpi-icon-box">
            <v-icon size="20" :color="netProfit >= 0 ? '#166534' : '#991b1b'">
              {{ netProfit >= 0 ? 'mdi-trending-up' : 'mdi-trending-down' }}
            </v-icon>
          </div>
          <span
            class="kpi-pill"
            :class="netProfit >= 0 ? 'pill-profit' : 'pill-loss'"
          >
            {{ netProfit >= 0 ? 'Net Profit' : 'Net Loss' }}
          </span>
        </div>
        <div class="kpi-label">{{ netProfit >= 0 ? 'NET PROFIT' : 'NET LOSS' }}</div>
        <div
          class="kpi-value"
          :class="netProfit >= 0 ? 'text-profit' : 'text-loss'"
        >
          ₹ {{ formatCurrency(Math.abs(netProfit)) }}
        </div>
        <div class="kpi-footer">
          <span v-if="netMarginPct !== null" class="kpi-meta font-weight-bold">
            Margin: {{ netMarginPct }}%
          </span>
          <span v-else class="kpi-meta">Current period</span>
        </div>
      </div>

      <!-- 4. Expense Ratio -->
      <div class="pnl-kpi-card">
        <div class="kpi-top">
          <div class="kpi-icon-box">
            <v-icon size="20" color="#334155">mdi-percent-outline</v-icon>
          </div>
          <span class="kpi-pill">Cost Ratio</span>
        </div>
        <div class="kpi-label">EXPENSE RATIO</div>
        <div class="kpi-value">
          {{ expenseRatioPct !== null ? expenseRatioPct + '%' : '—' }}
        </div>
        <div class="kpi-footer">
          <span class="kpi-meta">
            {{ getHealthStatusText() }}
          </span>
        </div>
      </div>
    </section>

    <!-- 4. COMPARISON BAR (SUBTLE & PROFESSIONAL) -->
    <section class="pnl-comparison-strip mb-4" v-if="totalIncome > 0 || totalExpense > 0">
      <div class="comparison-bar-header">
        <div class="bar-legend-item">
          <span class="legend-indicator dot-income"></span>
          <span class="legend-name">Income ({{ incomeBarPct }}%):</span>
          <span class="legend-val">₹ {{ formatCurrency(totalIncome) }}</span>
        </div>
        <div class="bar-legend-center">
          <span class="equation-text">
            {{ netProfit >= 0 ? 'Net Profit: ₹ ' + formatCurrency(netProfit) : 'Net Loss: ₹ ' + formatCurrency(Math.abs(netProfit)) }}
          </span>
        </div>
        <div class="bar-legend-item">
          <span class="legend-indicator dot-expense"></span>
          <span class="legend-name">Expenses ({{ expenseBarPct }}%):</span>
          <span class="legend-val">₹ {{ formatCurrency(totalExpense) }}</span>
        </div>
      </div>
      <div class="ratio-progress-track">
        <div
          class="ratio-progress-segment income-bar"
          :style="{ width: incomeBarPct + '%' }"
          :title="'Income: ' + incomeBarPct + '%'"
        ></div>
        <div
          class="ratio-progress-segment expense-bar"
          :style="{ width: expenseBarPct + '%' }"
          :title="'Expense: ' + expenseBarPct + '%'"
        ></div>
      </div>
    </section>

    <!-- 5. MAIN SPLIT TABLES (INCOME VS EXPENSES) -->
    <section class="pnl-tables-grid mb-4">
      <!-- Left: Income Table Card -->
      <div class="financial-card">
        <div class="card-top-bar">
          <div class="bar-title-wrap">
            <h2 class="head-card-title">Income</h2>
            <span class="head-count-badge">{{ incomeRows.length }}</span>
          </div>

          <div class="bar-controls-wrap">
            <div class="mini-search-box">
              <v-icon size="14" color="#94a3b8">mdi-magnify</v-icon>
              <input
                v-model="incomeSearch"
                type="text"
                placeholder="Filter income..."
                class="mini-search-input"
              />
              <v-icon
                v-if="incomeSearch"
                size="14"
                color="#94a3b8"
                class="cursor-pointer"
                @click="incomeSearch = ''"
              >
                mdi-close
              </v-icon>
            </div>

            <button
              class="mini-sort-btn"
              @click="toggleIncomeSort()"
              :title="'Sort: ' + incomeSort"
            >
              <v-icon size="15" color="#475569">
                {{ incomeSort === 'amount-desc' ? 'mdi-sort-numeric-descending' : 'mdi-sort-alphabetical-ascending' }}
              </v-icon>
            </button>
          </div>
        </div>

        <div class="table-scroll-container">
          <table class="financial-statement-table">
            <thead>
              <tr>
                <th class="text-left">Income Head</th>
                <th class="text-center" style="width: 90px;">Share</th>
                <th class="text-right" style="width: 150px;">Amount (₹)</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(item, i) in filteredIncomeRows" :key="item._id || i" class="table-row">
                <td class="text-left">
                  <span class="account-name-text">{{ item.name }}</span>
                </td>
                <td class="text-center">
                  <span class="share-badge">
                    {{ calculateShare(item.amount, totalIncome) }}%
                  </span>
                </td>
                <td class="text-right num-cell font-weight-bold">
                  ₹ {{ formatCurrency(item.amount) }}
                </td>
              </tr>

              <!-- Empty State -->
              <tr v-if="filteredIncomeRows.length === 0">
                <td colspan="3" class="empty-state-cell">
                  <div class="empty-state-inner">
                    <v-icon size="30" color="#94a3b8" class="mb-2">mdi-receipt-text-outline</v-icon>
                    <p class="empty-title">
                      {{ incomeSearch ? 'No matching income heads' : 'No income records found' }}
                    </p>
                    <p class="empty-desc">
                      {{ incomeSearch ? 'Try a different search query.' : 'No revenue records for the selected date range.' }}
                    </p>
                  </div>
                </td>
              </tr>
            </tbody>
            <tfoot>
              <tr class="summary-footer-row">
                <td class="text-left footer-label">TOTAL INCOME</td>
                <td class="text-center">
                  <span class="footer-count-chip">{{ incomeRows.length }} Heads</span>
                </td>
                <td class="text-right footer-amount num-cell">
                  ₹ {{ formatCurrency(totalIncome) }}
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>

      <!-- Right: Expense Table Card -->
      <div class="financial-card">
        <div class="card-top-bar">
          <div class="bar-title-wrap">
            <h2 class="head-card-title">Expenses</h2>
            <span class="head-count-badge">{{ expenseRows.length }}</span>
          </div>

          <div class="bar-controls-wrap">
            <div class="mini-search-box">
              <v-icon size="14" color="#94a3b8">mdi-magnify</v-icon>
              <input
                v-model="expenseSearch"
                type="text"
                placeholder="Filter expense..."
                class="mini-search-input"
              />
              <v-icon
                v-if="expenseSearch"
                size="14"
                color="#94a3b8"
                class="cursor-pointer"
                @click="expenseSearch = ''"
              >
                mdi-close
              </v-icon>
            </div>

            <button
              class="mini-sort-btn"
              @click="toggleExpenseSort()"
              :title="'Sort: ' + expenseSort"
            >
              <v-icon size="15" color="#475569">
                {{ expenseSort === 'amount-desc' ? 'mdi-sort-numeric-descending' : 'mdi-sort-alphabetical-ascending' }}
              </v-icon>
            </button>
          </div>
        </div>

        <div class="table-scroll-container">
          <table class="financial-statement-table">
            <thead>
              <tr>
                <th class="text-left">Expense Head</th>
                <th class="text-center" style="width: 90px;">Share</th>
                <th class="text-right" style="width: 150px;">Amount (₹)</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(item, i) in filteredExpenseRows" :key="item._id || i" class="table-row">
                <td class="text-left">
                  <span class="account-name-text">{{ item.name }}</span>
                </td>
                <td class="text-center">
                  <span class="share-badge">
                    {{ calculateShare(item.amount, totalExpense) }}%
                  </span>
                </td>
                <td class="text-right num-cell font-weight-bold">
                  ₹ {{ formatCurrency(item.amount) }}
                </td>
              </tr>

              <!-- Empty State -->
              <tr v-if="filteredExpenseRows.length === 0">
                <td colspan="3" class="empty-state-cell">
                  <div class="empty-state-inner">
                    <v-icon size="30" color="#94a3b8" class="mb-2">mdi-receipt-text-outline</v-icon>
                    <p class="empty-title">
                      {{ expenseSearch ? 'No matching expense heads' : 'No expense records found' }}
                    </p>
                    <p class="empty-desc">
                      {{ expenseSearch ? 'Try a different search query.' : 'No expense debits recorded for the selected date range.' }}
                    </p>
                  </div>
                </td>
              </tr>
            </tbody>
            <tfoot>
              <tr class="summary-footer-row">
                <td class="text-left footer-label">TOTAL EXPENSES</td>
                <td class="text-center">
                  <span class="footer-count-chip">{{ expenseRows.length }} Heads</span>
                </td>
                <td class="text-right footer-amount num-cell">
                  ₹ {{ formatCurrency(totalExpense) }}
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>
    </section>

    <!-- 6. GRAND EXECUTIVE SUMMARY RIBBON (CLEAN & SUBTLE) -->
    <section class="grand-summary-banner">
      <div class="banner-left">
        <div class="banner-icon-container">
          <v-icon size="24" :color="netProfit >= 0 ? '#166534' : '#991b1b'">
            {{ netProfit >= 0 ? 'mdi-trending-up' : 'mdi-trending-down' }}
          </v-icon>
        </div>
        <div class="banner-text">
          <div class="banner-badge-line">
            <span
              class="banner-badge"
              :class="netProfit >= 0 ? 'badge-profit' : 'badge-loss'"
            >
              {{ netProfit >= 0 ? 'NET PROFIT' : 'NET LOSS' }}
            </span>
            <span v-if="netMarginPct !== null" class="banner-margin-tag">
              Net Margin: {{ netMarginPct }}%
            </span>
          </div>
          <p class="banner-formula-text">
            Net Result = Total Revenue (₹ {{ formatCurrency(totalIncome) }}) &minus; Total Expenses (₹ {{ formatCurrency(totalExpense) }})
          </p>
        </div>
      </div>

      <div class="banner-right">
        <span class="banner-amount-label">
          {{ netProfit >= 0 ? 'NET PROFIT' : 'NET LOSS' }}
        </span>
        <div
          class="banner-amount-val num-cell"
          :class="netProfit >= 0 ? 'text-profit' : 'text-loss'"
        >
          ₹ {{ formatCurrency(Math.abs(netProfit)) }}
        </div>
      </div>
    </section>
  </div>
</template>

<script>
import axios from "axios";

export default {
  name: "ProfitAndLoss",
  data() {
    var today = new Date(Date.now() - new Date().getTimezoneOffset() * 60000)
      .toISOString()
      .substr(0, 10);
    return {
      appLoading: false,
      isDownloading: false,
      ServerError: false,
      showsnackbar: false,
      timeout: 2500,
      msg: null,
      fromMenu: false,
      toMenu: false,
      from: today,
      to: today,
      incomeRows: [],
      expenseRows: [],
      totalIncome: 0,
      totalExpense: 0,
      netProfit: 0,
      incomeSearch: "",
      expenseSearch: "",
      incomeSort: "amount-desc",
      expenseSort: "amount-desc",
    };
  },
  computed: {
    filteredIncomeRows() {
      let rows = [...this.incomeRows];
      if (this.incomeSearch && this.incomeSearch.trim()) {
        const q = this.incomeSearch.toLowerCase().trim();
        rows = rows.filter((r) => r.name && r.name.toLowerCase().includes(q));
      }
      if (this.incomeSort === "amount-desc") {
        rows.sort((a, b) => b.amount - a.amount);
      } else if (this.incomeSort === "name-asc") {
        rows.sort((a, b) => (a.name || "").localeCompare(b.name || ""));
      }
      return rows;
    },
    filteredExpenseRows() {
      let rows = [...this.expenseRows];
      if (this.expenseSearch && this.expenseSearch.trim()) {
        const q = this.expenseSearch.toLowerCase().trim();
        rows = rows.filter((r) => r.name && r.name.toLowerCase().includes(q));
      }
      if (this.expenseSort === "amount-desc") {
        rows.sort((a, b) => b.amount - a.amount);
      } else if (this.expenseSort === "name-asc") {
        rows.sort((a, b) => (a.name || "").localeCompare(b.name || ""));
      }
      return rows;
    },
    netMarginPct() {
      if (!this.totalIncome || this.totalIncome <= 0) return null;
      return ((this.netProfit / this.totalIncome) * 100).toFixed(1);
    },
    expenseRatioPct() {
      if (!this.totalIncome || this.totalIncome <= 0) {
        if (this.totalExpense > 0) return ">100";
        return null;
      }
      return ((this.totalExpense / this.totalIncome) * 100).toFixed(1);
    },
    incomeBarPct() {
      const sum = (this.totalIncome || 0) + (this.totalExpense || 0);
      if (sum <= 0) return 50;
      return Math.round(((this.totalIncome || 0) / sum) * 100);
    },
    expenseBarPct() {
      const sum = (this.totalIncome || 0) + (this.totalExpense || 0);
      if (sum <= 0) return 50;
      return 100 - this.incomeBarPct;
    },
  },
  mounted() {
    this.fetchProfitAndLoss();
  },
  methods: {
    formatCurrency(val) {
      if (val === null || val === undefined || isNaN(val) || val === "") return "0.00";
      return Number(val).toLocaleString("en-IN", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    },
    formatDateDisplay(dateStr) {
      if (!dateStr) return "";
      try {
        const parts = dateStr.split("-");
        if (parts.length === 3) {
          const d = new Date(parts[0], parts[1] - 1, parts[2]);
          return d.toLocaleDateString("en-IN", {
            day: "numeric",
            month: "short",
            year: "numeric",
          });
        }
        return dateStr;
      } catch (e) {
        return dateStr;
      }
    },
    calculateShare(amount, total) {
      if (!total || total <= 0 || !amount) return "0.0";
      return ((amount / total) * 100).toFixed(1);
    },
    getHealthStatusText() {
      if (!this.totalIncome || this.totalIncome <= 0) {
        return this.totalExpense > 0 ? "Expenses recorded" : "No transactions";
      }
      const ratio = (this.totalExpense / this.totalIncome) * 100;
      if (ratio < 50) return "Healthy margin";
      if (ratio <= 100) return "Moderate expense";
      return "Operating loss";
    },
    onFromDateChange(val) {
      if (this.$refs.fromMenu) {
        this.$refs.fromMenu.save(val);
      }
      this.fetchProfitAndLoss();
    },
    onToDateChange(val) {
      if (this.$refs.toMenu) {
        this.$refs.toMenu.save(val);
      }
      this.fetchProfitAndLoss();
    },
    toggleIncomeSort() {
      this.incomeSort = this.incomeSort === "amount-desc" ? "name-asc" : "amount-desc";
    },
    toggleExpenseSort() {
      this.expenseSort = this.expenseSort === "amount-desc" ? "name-asc" : "amount-desc";
    },
    fetchProfitAndLoss() {
      if (!this.from) return;
      if (!this.to) this.to = this.from;
      this.appLoading = true;
      axios({
        method: "get",
        url: "/accounting/profitandloss",
        headers: {
          "x-auth-token": localStorage.getItem("token"),
        },
        params: {
          from: this.from,
          to: this.to,
        },
      })
        .then((res) => {
          this.appLoading = false;
          if (res.data.status) {
            this.incomeRows = res.data.incomeRows || [];
            this.expenseRows = res.data.expenseRows || [];
            this.totalIncome = res.data.totalIncome || 0;
            this.totalExpense = res.data.totalExpense || 0;
            this.netProfit = res.data.netProfit || 0;
          } else {
            this.msg = res.data.msg || "Failed to load Profit & Loss";
            this.showsnackbar = true;
          }
        })
        .catch((err) => {
          this.appLoading = false;
          this.ServerError = true;
          console.error(err);
        });
    },
    download() {
      if (!this.from || !this.to) {
        this.msg = "Please choose both dates";
        this.showsnackbar = true;
        return;
      }
      this.isDownloading = true;
      axios({
        method: "get",
        url: "/download/profitandloss",
        headers: {
          "x-auth-token": localStorage.getItem("token"),
        },
        params: {
          from: this.from,
          to: this.to,
        },
        responseType: "blob",
      })
        .then((response) => {
          this.isDownloading = false;
          const url = window.URL.createObjectURL(new Blob([response.data]));
          const link = document.createElement("a");
          link.href = url;
          link.setAttribute("download", `ProfitAndLoss_${this.from}_to_${this.to}.pdf`);
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
        })
        .catch((err) => {
          this.isDownloading = false;
          this.ServerError = true;
          console.error(err);
        });
    },
  },
};
</script>

<style scoped>
/* Page Layout Container */
.pnl-page-container {
  padding: 4px 0 40px 0;
  width: 100%;
  max-width: 1600px;
  margin: 0 auto;
  font-family: 'Plus Jakarta Sans', 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
  color: #0f172a;
}

/* 1. Hero Header Section */
.pnl-hero-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 16px;
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 20px 24px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.03);
}

.pnl-eyebrow {
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 1px;
  color: #64748b;
  margin-bottom: 4px;
  text-transform: uppercase;
}

.pnl-main-title {
  font-size: 24px;
  font-weight: 700;
  color: #0f172a;
  letter-spacing: -0.3px;
  margin: 0;
  line-height: 1.2;
}

.date-badge-pill {
  display: inline-flex;
  align-items: center;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  color: #334155;
  font-size: 12px;
  font-weight: 600;
  padding: 3px 10px;
  border-radius: 6px;
  margin-left: 8px;
}

.pnl-subtitle {
  font-size: 13px;
  color: #64748b;
  margin: 4px 0 0 0;
}

.pnl-hero-actions {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
}

.action-btn {
  height: 38px;
  padding: 0 16px;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 600;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.15s ease;
}

.outline-btn {
  background: #ffffff;
  border: 1px solid #cbd5e1;
  color: #334155;
}

.outline-btn:hover {
  background: #f8fafc;
  border-color: #94a3b8;
}

.primary-dark-btn {
  background: #1e293b;
  color: #ffffff;
  border: 1px solid #1e293b;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.primary-dark-btn:hover:not(:disabled) {
  background: #0f172a;
  border-color: #0f172a;
}

.primary-dark-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

/* 2. Filter Strip */
.pnl-filter-card {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  flex-wrap: wrap;
  gap: 14px;
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  padding: 10px 16px;
}

.filter-card-right {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
}

.date-field-wrapper {
  display: flex;
  align-items: center;
  gap: 6px;
}

.date-label {
  font-size: 12px;
  font-weight: 600;
  color: #64748b;
}

.custom-date-box {
  display: flex;
  align-items: center;
  background: #ffffff;
  border: 1px solid #cbd5e1;
  border-radius: 6px;
  padding: 5px 12px;
  font-size: 12.5px;
  font-weight: 600;
  color: #0f172a;
  cursor: pointer;
  transition: all 0.15s ease;
}

.custom-date-box:hover {
  border-color: #64748b;
}

.date-box-val {
  min-width: 82px;
  text-align: center;
}

/* 3. KPI Grid */
.pnl-kpi-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 14px;
}

@media (max-width: 1100px) {
  .pnl-kpi-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (max-width: 640px) {
  .pnl-kpi-grid {
    grid-template-columns: 1fr;
  }
}

.pnl-kpi-card {
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  padding: 16px 18px;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.02);
  transition: border-color 0.15s ease;
}

.pnl-kpi-card:hover {
  border-color: #cbd5e1;
}

.kpi-top {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 10px;
}

.kpi-icon-box {
  width: 34px;
  height: 34px;
  border-radius: 6px;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  display: flex;
  align-items: center;
  justify-content: center;
}

.kpi-pill {
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  padding: 2px 8px;
  border-radius: 4px;
  background: #f1f5f9;
  color: #475569;
}

.pill-profit {
  background: #f0fdf4;
  color: #166534;
}

.pill-loss {
  background: #fef2f2;
  color: #991b1b;
}

.kpi-label {
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.5px;
  color: #64748b;
  margin-bottom: 4px;
  text-transform: uppercase;
}

.kpi-value {
  font-size: 22px;
  font-weight: 700;
  letter-spacing: -0.3px;
  line-height: 1.2;
  margin-bottom: 8px;
  color: #0f172a;
  font-feature-settings: 'tnum';
  font-variant-numeric: tabular-nums;
}

.text-profit {
  color: #166534 !important;
}

.text-loss {
  color: #991b1b !important;
}

.num-cell {
  font-feature-settings: 'tnum';
  font-variant-numeric: tabular-nums;
  color: #0f172a;
}

.kpi-footer {
  border-top: 1px solid #f1f5f9;
  padding-top: 8px;
}

.kpi-meta {
  font-size: 12px;
  color: #64748b;
  display: flex;
  align-items: center;
}

/* 4. Comparison Strip (Subtle) */
.pnl-comparison-strip {
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  padding: 14px 18px;
}

.comparison-bar-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 10px;
  flex-wrap: wrap;
  gap: 8px;
}

.bar-legend-item {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12.5px;
}

.legend-indicator {
  width: 8px;
  height: 8px;
  border-radius: 2px;
  display: inline-block;
}

.dot-income {
  background: #334155;
}

.dot-expense {
  background: #94a3b8;
}

.legend-name {
  color: #64748b;
}

.legend-val {
  font-weight: 700;
  color: #0f172a;
}

.equation-text {
  font-size: 12px;
  font-weight: 600;
  color: #475569;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  padding: 2px 10px;
  border-radius: 4px;
}

.ratio-progress-track {
  height: 8px;
  background: #f1f5f9;
  border-radius: 4px;
  overflow: hidden;
  display: flex;
  width: 100%;
}

.ratio-progress-segment {
  height: 100%;
}

.income-bar {
  background: #334155;
}

.expense-bar {
  background: #94a3b8;
}

/* 5. Split Tables Grid */
.pnl-tables-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 16px;
}

@media (max-width: 960px) {
  .pnl-tables-grid {
    grid-template-columns: 1fr;
  }
}

.financial-card {
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.card-top-bar {
  padding: 12px 16px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  border-bottom: 1px solid #e2e8f0;
  background: #f8fafc;
  flex-wrap: wrap;
  gap: 10px;
}

.bar-title-wrap {
  display: flex;
  align-items: center;
  gap: 8px;
}

.head-card-title {
  font-size: 14px;
  font-weight: 700;
  color: #0f172a;
  margin: 0;
  letter-spacing: -0.2px;
}

.head-count-badge {
  font-size: 11px;
  font-weight: 600;
  background: #ffffff;
  border: 1px solid #cbd5e1;
  color: #475569;
  padding: 1px 6px;
  border-radius: 4px;
}

.bar-controls-wrap {
  display: flex;
  align-items: center;
  gap: 6px;
}

.mini-search-box {
  display: flex;
  align-items: center;
  background: #ffffff;
  border: 1px solid #cbd5e1;
  border-radius: 6px;
  padding: 4px 8px;
  width: 150px;
  gap: 4px;
  transition: border-color 0.15s ease;
}

.mini-search-box:focus-within {
  border-color: #0f172a;
}

.mini-search-input {
  border: none;
  outline: none;
  background: transparent;
  font-size: 11.5px;
  color: #0f172a;
  width: 100%;
}

.mini-sort-btn {
  background: #ffffff;
  border: 1px solid #cbd5e1;
  border-radius: 6px;
  width: 28px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.15s ease;
}

.mini-sort-btn:hover {
  background: #f1f5f9;
  border-color: #94a3b8;
}

.table-scroll-container {
  overflow-x: auto;
  max-height: 480px;
  overflow-y: auto;
}

.financial-statement-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
}

.financial-statement-table th {
  background: #ffffff;
  color: #64748b;
  font-weight: 600;
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  padding: 10px 16px;
  border-bottom: 1px solid #e2e8f0;
  position: sticky;
  top: 0;
  z-index: 2;
}

.financial-statement-table td {
  padding: 10px 16px;
  border-bottom: 1px solid #f1f5f9;
  color: #1e293b;
  vertical-align: middle;
}

.table-row:hover {
  background-color: #f8fafc;
}

.account-name-text {
  font-weight: 600;
  color: #0f172a;
  font-size: 13px;
}

.share-badge {
  font-size: 11px;
  font-weight: 600;
  padding: 1px 6px;
  border-radius: 4px;
  display: inline-block;
  background: #f8fafc;
  color: #64748b;
  border: 1px solid #e2e8f0;
}

/* Empty state */
.empty-state-cell {
  padding: 36px 16px !important;
  text-align: center;
  background: #ffffff;
}

.empty-state-inner {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

.empty-title {
  font-size: 13px;
  font-weight: 600;
  color: #64748b;
  margin: 0 0 2px 0;
}

.empty-desc {
  font-size: 12px;
  color: #94a3b8;
  margin: 0;
}

/* Summary Footer */
.summary-footer-row {
  border-top: 2px solid #cbd5e1;
  background: #f8fafc;
  font-weight: 700;
}

.footer-label {
  font-size: 12.5px;
  font-weight: 700;
  letter-spacing: 0.3px;
  color: #0f172a;
}

.footer-count-chip {
  font-size: 11px;
  font-weight: 600;
  background: #ffffff;
  padding: 1px 6px;
  border-radius: 4px;
  color: #64748b;
  border: 1px solid #e2e8f0;
}

.footer-amount {
  font-size: 14px;
  font-weight: 800;
  color: #0f172a;
}

/* 6. Grand Executive Summary Ribbon */
.grand-summary-banner {
  border-radius: 10px;
  padding: 18px 24px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 16px;
  background: #ffffff;
  border: 1px solid #cbd5e1;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.02);
}

.banner-left {
  display: flex;
  align-items: center;
  gap: 14px;
}

.banner-icon-container {
  width: 42px;
  height: 42px;
  border-radius: 8px;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  display: flex;
  align-items: center;
  justify-content: center;
}

.banner-badge-line {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 4px;
}

.banner-badge {
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.5px;
  padding: 2px 8px;
  border-radius: 4px;
}

.badge-profit {
  background: #f0fdf4;
  border: 1px solid #bbf7d0;
  color: #166534;
}

.badge-loss {
  background: #fef2f2;
  border: 1px solid #fecaca;
  color: #991b1b;
}

.banner-margin-tag {
  font-size: 12px;
  font-weight: 600;
  color: #334155;
  background: #f1f5f9;
  padding: 2px 8px;
  border-radius: 4px;
}

.banner-formula-text {
  font-size: 12.5px;
  font-weight: 500;
  color: #475569;
  margin: 0;
}

.banner-right {
  text-align: right;
}

.banner-amount-label {
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.5px;
  color: #64748b;
  display: block;
  margin-bottom: 2px;
}

.banner-amount-val {
  font-size: 26px;
  font-weight: 800;
  letter-spacing: -0.5px;
  line-height: 1.1;
  color: #0f172a;
}

/* Print Styles */
@media print {
  .pnl-hero-actions,
  .pnl-filter-card,
  .mini-search-box,
  .mini-sort-btn {
    display: none !important;
  }

  .pnl-page-container {
    padding: 0 !important;
    max-width: 100% !important;
  }

  .financial-card {
    box-shadow: none !important;
    border: 1px solid #ccc !important;
  }

  .table-scroll-container {
    max-height: none !important;
    overflow: visible !important;
  }

  .grand-summary-banner {
    box-shadow: none !important;
  }
}
</style>
